import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { theme } from '@openx/components-core/lib/theme/theme';
import type { TroubleshootingFunnelUpshotCodes } from '@openx/types/troubleshootingFunnel';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpshotCodesTranslations } from '../hooks/useUpshotCodesTranslations';

type UpshotCodesDisplayProps = {
  data: TroubleshootingFunnelUpshotCodes | null;
};

export const UpshotCodesDisplay = ({ data }: UpshotCodesDisplayProps) => {
  const { t } = useTranslation();

  const upshotDescriptions = useUpshotCodesTranslations();

  const sortedData = useMemo(() => {
    if (!data) return [];
    return Object.entries(data).sort(([, a], [, b]) => b.count - a.count);
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <Box
      sx={{
        columnGap: theme.spacing(3),
        display: 'grid',
        gridTemplateColumns: 'auto fit-content(100%) fit-content(100%)',
        rowGap: theme.spacing(0.5),
      }}
      data-test="funnel-step-upshot-codes"
    >
      {/* Header Row */}
      <Box>
        <Typography variant="body2Bold">{t('Reason')}</Typography>
      </Box>
      <Box>
        <Typography variant="body2Bold">{t('Bids')}</Typography>
      </Box>
      <Box>
        <Typography variant="body2Bold">{t('% Total')}</Typography>
      </Box>

      {/* Content Rows */}
      {sortedData.map(([key, value], index) => (
        <React.Fragment key={index}>
          <Tooltip placement="top-start" title={upshotDescriptions[key]}>
            <Box>
              <Typography variant="body2" data-test="reason">
                {t(key)}
              </Typography>
            </Box>
          </Tooltip>
          <Box>
            <Typography variant="body2" data-test="volume">
              {abbreviateNumericValue(value.count, { allowZero: true })}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" data-test="percentage">
              {abbreviateNumericValue(value.percentage, { allowZero: true, multiplier: 100 })}%
            </Typography>
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
};
